import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Snackbar,
    Alert,
    useMediaQuery,
    useTheme,
    Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import TelegramIcon from '@mui/icons-material/Telegram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

// Import sub-components
import {
    ContactPersonalInfo,
    ContactImportantDates,
    ContactTags,
    ContactConnections,
    ContactInteractions,
    ContactAISummary,
    ContactOpportunityContext
} from './ContactDetails';

import AddInteractionDialog from './AddInteractionDialog';
import { useSnackbar } from './ContactDetails/useSnackbar';
import { useContacts } from '../../contexts/ContactsContext';
import { contactsAPI } from '../../services/api';
import {
    getDialogStyles,
    getDialogTitleStyles,
    getTitleContainerStyles,
    getTitleNameContainerStyles,
    getTitleNameStyles,
    getTitleActionsContainerStyles,
    getIconButtonStyles,
    getDeleteIconButtonStyles,
    getCloseIconButtonStyles,
    getDialogContentStyles,
    getContentContainerStyles,
    getSectionContainerStyles,
    getDividerStyles
} from '../../theme/app.styles';

const ContactDetailsDialog = ({
    open,
    contact: initialContact,
    onClose,
    onEdit,
    onDelete,
    onArchive,
    onUnarchive,
    onAddInteraction,
    onViewRelatedContact,
    onContactUpdate,
    t = {},
    allContacts = []
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isAddInteractionOpen, setIsAddInteractionOpen] = useState(false);
    const [pendingInteractionNote, setPendingInteractionNote] = useState('');
    const [contact, setContact] = useState(initialContact);
    const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
    const { updateContact: updateContactInContext } = useContacts();

    // Update local contact state when prop changes
    useEffect(() => {
        if (initialContact) {
            setContact(initialContact);
        }
    }, [initialContact]);

    // Reset interaction dialog state when main dialog closes
    useEffect(() => {
        if (!open) {
            setIsAddInteractionOpen(false);
            setPendingInteractionNote('');
        }
    }, [open]);

    // Listen for event to open interaction dialog
    useEffect(() => {
        const handleTriggerAddInteraction = (event) => {
            const { contactId, suggestedNote } = event.detail;
            if (contact && contact._id === contactId && open) {
                setPendingInteractionNote(suggestedNote || '');
                setIsAddInteractionOpen(true);
            }
        };

        document.addEventListener('trigger-add-interaction', handleTriggerAddInteraction);
        return () => {
            document.removeEventListener('trigger-add-interaction', handleTriggerAddInteraction);
        };
    }, [contact, open]);

    const handleAddInteraction = async (contactId, interactionData, updatedContactData = null) => {
        if (!contactId || !interactionData) {
            console.error('Missing contactId or interactionData');
            return;
        }

        try {
            // Если нам передали обновленные данные контакта (с новыми связями)
            if (updatedContactData) {
                // Обновляем локальный state контакта
                setContact(prev => ({
                    ...prev,
                    relations: updatedContactData.relations
                }));
            }
            
            // Вызываем родительский обработчик для сохранения взаимодействия
            await onAddInteraction(contactId, interactionData, updatedContactData);

            // Reload fresh contact data to update the dialog immediately
            try {
                const response = await contactsAPI.getContact(contactId);
                if (response.data) {
                    setContact(response.data);
                }
            } catch (refreshError) {
                console.error('Error refreshing contact after interaction:', refreshError);
            }

            setIsAddInteractionOpen(false);
            setPendingInteractionNote('');
            showSnackbar(t.interactionAdded || 'Touch added', 'success');
        } catch (error) {
            console.error('Error adding touch:', error);
            showSnackbar(error.message || 'Error adding touch', 'error');
        }
    };

    const handleClose = () => {
        onClose();
    };

    const handleDelete = () => {
        onDelete(contact._id);
    };

    const handleContactUpdate = async (updatedContact) => {
        try {
            // Обновляем в контексте для реального времени
            if (updatedContact._id) {
                await updateContactInContext(updatedContact._id, updatedContact);
            }
            
            // Обновляем локальное состояние
            setContact(updatedContact);
            
            // Вызываем родительский колбэк
            if (onContactUpdate) {
                onContactUpdate(updatedContact);
            }
        } catch (error) {
            console.error('Error updating contact:', error);
            showSnackbar('Error updating contact', 'error');
        }
    };

    const handleSnackbar = (snackbarData) => {
        showSnackbar(snackbarData.message, snackbarData.severity);
    };

    const handleOpenAddInteraction = () => {
        setPendingInteractionNote('');
        setIsAddInteractionOpen(true);
    };

    if (!contact || !contact._id) return null;

    const formatPhone = (phone, countryPhoneCode) => {
        if (!phone) return null;
        let cleaned = phone.replace(/[\s\-()]/g, '');
        if (!cleaned.startsWith('+') && countryPhoneCode) {
            cleaned = '+' + countryPhoneCode + cleaned;
        }
        return cleaned;
    };

    const getTelegramUrl = () => {
        const tg = contact.socialMedia?.telegram;
        if (tg) {
            const username = tg.replace(/^@/, '');
            return `https://t.me/${username}`;
        }
        const phone = formatPhone(contact.phone, contact.countryPhoneCode);
        if (phone) {
            return `https://t.me/${phone.startsWith('+') ? phone : '+' + phone}`;
        }
        return null;
    };

    const hasPhone = !!contact.phone;
    const hasEmail = !!contact.email;
    const hasTelegram = !!(contact.socialMedia?.telegram || contact.phone);
    const hasAnyQuickAction = hasPhone || hasEmail || hasTelegram;

    const quickActionBase = {
        transition: 'transform 0.15s, opacity 0.15s',
        padding: '6px',
        '&:hover': {
            transform: 'scale(1.15)',
            opacity: 0.85,
        },
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                fullScreen={isMobile}
                maxWidth="md"
                PaperProps={{
                    sx: getDialogStyles(theme)
                }}
            >
                <DialogTitle sx={getDialogTitleStyles(theme)}>
                    <Box sx={getTitleContainerStyles()}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0, flex: 1 }}>
                            <Box sx={getTitleNameContainerStyles()}>
                                <Typography
                                    variant="h5"
                                    sx={getTitleNameStyles(theme)}
                                >
                                    {contact.name || ''}
                                </Typography>
                                {contact.isConnector && (
                                    <span title="Connector - can introduce to new people">
                                        ⭐️
                                    </span>
                                )}
                                {contact.isBrain && !contact.isConnector && (
                                    <span title="Brain - accumulates knowledge and experience">
                                        🧠
                                    </span>
                                )}
                            </Box>
                            <ContactTags
                                contact={contact}
                                onContactUpdate={handleContactUpdate}
                                onSnackbar={handleSnackbar}
                                t={t}
                                compact
                            />
                        </Box>
                        {/* Quick Action Buttons */}
                        {hasAnyQuickAction ? (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 1 }}>
                                {hasPhone && (
                                    <>
                                        <IconButton
                                            title="WhatsApp"
                                            sx={quickActionBase}
                                            onClick={() => {
                                                const phone = formatPhone(contact.phone, contact.countryPhoneCode);
                                                const digits = phone.replace(/[^0-9]/g, '');
                                                window.open(`https://wa.me/${digits}`, '_blank');
                                            }}
                                        >
                                            <WhatsAppIcon sx={{ fontSize: 23, color: '#25D366' }} />
                                        </IconButton>
                                        <IconButton
                                            title="Call"
                                            sx={quickActionBase}
                                            onClick={() => {
                                                window.location.href = `tel:${formatPhone(contact.phone, contact.countryPhoneCode)}`;
                                            }}
                                        >
                                            <PhoneIcon sx={{ fontSize: 23, color: '#4CAF50' }} />
                                        </IconButton>
                                    </>
                                )}
                                {hasTelegram && (
                                    <IconButton
                                        title="Telegram"
                                        sx={quickActionBase}
                                        onClick={() => {
                                            window.open(getTelegramUrl(), '_blank');
                                        }}
                                    >
                                        <TelegramIcon sx={{ fontSize: 23, color: '#26A5E4' }} />
                                    </IconButton>
                                )}
                                {hasEmail && (
                                    <IconButton
                                        title="Email"
                                        sx={quickActionBase}
                                        onClick={() => {
                                            window.location.href = `mailto:${contact.email}`;
                                        }}
                                    >
                                        <EmailIcon sx={{ fontSize: 23, color: '#EA4335' }} />
                                    </IconButton>
                                )}
                            </Box>
                        ) : (
                            <Box sx={{ ml: 0.5 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: theme.palette.text.disabled,
                                        cursor: 'pointer',
                                        '&:hover': { color: theme.palette.primary.main, textDecoration: 'underline' },
                                    }}
                                    onClick={() => onEdit(contact)}
                                >
                                    {t.addContactDetails || 'Add contact details'}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={getTitleActionsContainerStyles()}>
                            <IconButton 
                                onClick={() => onEdit(contact)}
                                size="small"
                                sx={getIconButtonStyles(theme)}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                            {onArchive && onUnarchive && (
                                contact.archived ? (
                                    <IconButton
                                        onClick={() => onUnarchive(contact)}
                                        title={t.unarchiveContact || 'Unarchive contact'}
                                        size="small"
                                        sx={getIconButtonStyles(theme)}
                                    >
                                        <UnarchiveIcon fontSize="small" />
                                    </IconButton>
                                ) : (
                                    <IconButton
                                        onClick={() => onArchive(contact)}
                                        title={t.archiveContact || 'Archive contact'}
                                        size="small"
                                        sx={getIconButtonStyles(theme)}
                                    >
                                        <ArchiveIcon fontSize="small" />
                                    </IconButton>
                                )
                            )}
                            {onDelete && (
                                <IconButton
                                    onClick={handleDelete}
                                    size="small"
                                    sx={getDeleteIconButtonStyles(theme)}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            )}
                            <IconButton 
                                onClick={handleClose}
                                size="small"
                                sx={getCloseIconButtonStyles(theme)}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent 
                    dividers
                    sx={getDialogContentStyles()}
                >
                    <Box sx={getContentContainerStyles()}>
                        <Box>
                            {/* Personal Info */}
                            <Box sx={getSectionContainerStyles()}>
                                <ContactPersonalInfo 
                                    contact={contact} 
                                    onContactUpdate={handleContactUpdate}
                                    onSnackbar={handleSnackbar}
                                    t={t} 
                                />
                            </Box>
                            <Divider sx={getDividerStyles()} />

                            {/* AI Summary */}
                            {contact.interactions && contact.interactions.length > 0 && (
                                <>
                                    <Box sx={getSectionContainerStyles()}>
                                        <ContactAISummary
                                            contact={contact}
                                            onContactUpdate={handleContactUpdate}
                                        />
                                    </Box>
                                    <Divider sx={getDividerStyles()} />
                                </>
                            )}

                            {/* Important Dates */}
                            <Box sx={getSectionContainerStyles()}>
                                <ContactImportantDates 
                                    contact={contact} 
                                    onContactUpdate={handleContactUpdate}
                                    onSnackbar={handleSnackbar}
                                    t={t} 
                                />
                            </Box>
                            <Divider sx={getDividerStyles()} />

                            {/* Connections */}
                            <Box sx={getSectionContainerStyles()}>
                                <ContactConnections 
                                    contact={contact}
                                    allContacts={allContacts}
                                    onContactUpdate={handleContactUpdate}
                                    onRelatedContactClick={onViewRelatedContact}
                                    onSnackbar={handleSnackbar}
                                    t={t}
                                />
                            </Box>
                            <Divider sx={getDividerStyles()} />

                            {/* Opportunity Context */}
                            <Box sx={getSectionContainerStyles()}>
                                <ContactOpportunityContext
                                    contact={contact}
                                    onContactUpdate={handleContactUpdate}
                                />
                            </Box>
                            <Divider sx={getDividerStyles()} />

                            {/* Touches */}
                            <Box sx={getSectionContainerStyles()}>
                                <ContactInteractions 
                                    contact={contact}
                                    onAddInteractionClick={handleOpenAddInteraction}
                                    onContactUpdate={handleContactUpdate}
                                    onSnackbar={handleSnackbar}
                                    t={t}
                                />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            <AddInteractionDialog
                open={isAddInteractionOpen}
                onClose={() => {
                    setIsAddInteractionOpen(false);
                    setPendingInteractionNote('');
                }}
                onAdd={handleAddInteraction}
                contactId={contact?._id}
                language={t.language || 'en'}
                initialNotes={pendingInteractionNote}
                title={pendingInteractionNote ? 'Log what happened' : undefined}
                helperText={pendingInteractionNote
                    ? 'Adjust this memory after the real touch. What did you learn? What should future-you remember?'
                    : undefined}
            />

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={hideSnackbar}
            >
                <Alert
                    onClose={hideSnackbar}
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ContactDetailsDialog; 
